import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { getApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdAbsencesCompanyPaginated } from '@/api/generated/company-management-user/company-management-user'
import { getApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdVacationquotaCurrent } from '@/api/generated/company-user/company-user'
import { AbsenceApproval, AbsenceReason } from '@/api/model'
import type { AbsenceModel, EmployeeUserResponse, VacationQuotaModel } from '@/api/model'

/** Computed absence metrics of an employee (current year). */
export interface EmployeeAbsenceSummary {
  /** Remaining vacation (current quota surplus). */
  remainingVacationDays: number | null
  /** Carryover from the previous year. */
  surplusFromPrevious: number | null
  surplusValidUntil: string | null
  /** Sick days. */
  illnessDays: number
  /** Planned vacation (requested, still pending). */
  plannedVacationDays: number
  /** Taken vacation (approved). */
  takenVacationDays: number
  /** Open approvals (requests with status `Pending`). */
  pendingApprovals: number
}

/** Loading state + result per employee, so the table can react per row. */
export interface AbsenceSummaryState {
  summary?: EmployeeAbsenceSummary
  isPending: boolean
  isError: boolean
}

const ILLNESS_REASONS = new Set<string>([
  AbsenceReason.Illness,
  AbsenceReason.IllnessWithoutDoctorsNotice,
  AbsenceReason.ChildSick,
])

/** Inclusively counted calendar days of a sick absence (0 for another reason). */
function countIllnessDays(absence: AbsenceModel): number {
  if (!ILLNESS_REASONS.has(absence.absenceReason)) return 0
  const start = dayjs(absence.startTimestamp).startOf('day')
  const end = dayjs(absence.endTimestamp).startOf('day')
  return end.diff(start, 'day') + 1
}

/** Vacation days of an absence; half days count as 0.5 (0 for another reason). */
function countVacationDays(absence: AbsenceModel): number {
  const reason = absence.absenceReason
  if (reason !== AbsenceReason.Vacation && reason !== AbsenceReason.VacationHalfDay) return 0
  const start = dayjs(absence.startTimestamp).startOf('day')
  const end = dayjs(absence.endTimestamp).startOf('day')
  const days = end.diff(start, 'day') + 1
  return reason === AbsenceReason.VacationHalfDay ? 0.5 * days : days
}

const ABSENCE_PAGE_SIZE = 200

/** Load all absences of an employee within the date range (server-side paginated). */
async function fetchAllAbsences(
  serviceModuleId: number,
  employeeUserId: string,
  startDate: string,
  endDate: string,
): Promise<AbsenceModel[]> {
  let offset = 0
  let items: AbsenceModel[] = []
  let totalCount = 0

  do {
    const res = await getApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdAbsencesCompanyPaginated(
      serviceModuleId,
      { Offset: offset, Limit: ABSENCE_PAGE_SIZE, employeeUserId, startDate, endDate },
    )
    const page = res.data
    const pageItems = page?.items ?? []
    if (pageItems.length === 0) break
    items = items.concat(pageItems)
    totalCount = page?.totalCount ?? 0
    offset += ABSENCE_PAGE_SIZE
  } while (items.length < totalCount)

  // The backend may return entries of others — defensively filter to the employee.
  return items.filter((a) => a.employeeUserId === employeeUserId)
}

/** Build an employee's metrics from absences + the current vacation quota. */
async function loadSummary(
  serviceModuleId: number,
  employeeId: string,
  startDate: string,
  endDate: string,
): Promise<EmployeeAbsenceSummary> {
  const [absences, quota] = await Promise.all([
    fetchAllAbsences(serviceModuleId, employeeId, startDate, endDate),
    getApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdVacationquotaCurrent(serviceModuleId, {
      employeeId,
      loadSurplus: true,
    })
      .then((r) => (r.data || undefined) as VacationQuotaModel | undefined)
      .catch(() => undefined),
  ])

  let illnessDays = 0
  let plannedVacationDays = 0
  let takenVacationDays = 0
  let pendingApprovals = 0

  for (const absence of absences) {
    illnessDays += countIllnessDays(absence)
    if (absence.absenceApproval === AbsenceApproval.Pending) pendingApprovals += 1
    const vacationDays = countVacationDays(absence)
    if (vacationDays > 0) {
      if (absence.absenceApproval === AbsenceApproval.Approved) takenVacationDays += vacationDays
      else if (absence.absenceApproval === AbsenceApproval.Pending) plannedVacationDays += vacationDays
    }
  }

  return {
    remainingVacationDays: quota?.surplus ?? null,
    surplusFromPrevious: quota?.surplusFromPrevious ?? null,
    surplusValidUntil: quota?.surplusValidUntil ?? null,
    illnessDays,
    plannedVacationDays,
    takenVacationDays,
    pendingApprovals,
  }
}

/**
 * Loads the absence metrics for a list of employees via fan-out — one
 * React Query per employee (absences of the current year + vacation quota),
 * each bundled in one query. Returns a map (employee id → state),
 * so the table can look up loading state/value per row.
 */
export function useAbsenceSummaries(serviceModuleId: number, employees: EmployeeUserResponse[]) {
  const { startDate, endDate } = useMemo(() => {
    const now = dayjs()
    return {
      startDate: now.startOf('year').format('YYYY-MM-DD'),
      endDate: now.endOf('year').format('YYYY-MM-DD'),
      year: now.year(),
    }
  }, [])

  const results = useQueries({
    queries: employees.map((employee) => ({
      queryKey: ['absence-summary', serviceModuleId, employee.id, startDate] as const,
      queryFn: () => loadSummary(serviceModuleId, employee.id ?? '', startDate, endDate),
      enabled: !!employee.id,
      staleTime: 60_000,
    })),
  })

  const summariesById = useMemo(() => {
    const map = new Map<string, AbsenceSummaryState>()
    employees.forEach((employee, index) => {
      if (!employee.id) return
      const result = results[index]
      map.set(employee.id, {
        summary: result?.data,
        isPending: result?.isPending ?? false,
        isError: result?.isError ?? false,
      })
    })
    return map
  }, [employees, results])

  return { summariesById }
}
